import React, { useState } from 'react';

import { AlertCircle, Plus, Trash2 } from 'lucide-react';

import type { RevenueConfigTierInput, RiskTier } from '../../../api';

export interface RevenueConfiguration {
	name: string;
	description?: string;
	is_active: boolean;
	partner_revenue_share_percent?: number | null;
	tiers: {
		LOW: RevenueConfigTierInput;
		MEDIUM: RevenueConfigTierInput;
		HIGH: RevenueConfigTierInput;
		OTHER?: RevenueConfigTierInput;
	};
}

interface RiskTierStepProps {
	configuration: RevenueConfiguration;
	onUpdate: (updates: Partial<RevenueConfiguration>) => void;
	hideNavigation?: boolean;
}

const RISK_TIERS: {
	tier: RiskTier;
	label: string;
	description: string;
	color: string;
}[] = [
	{
		tier: 'LOW',
		label: 'Low Risk',
		description:
			'Merchants with low chargeback rates and stable processing history',
		color: 'border-green-300 bg-green-50',
	},
	{
		tier: 'MEDIUM',
		label: 'Medium Risk',
		description: 'Merchants with moderate risk profiles',
		color: 'border-yellow-300 bg-yellow-50',
	},
	{
		tier: 'HIGH',
		label: 'High Risk',
		description:
			'Merchants with higher risk profiles or specialized industries',
		color: 'border-red-300 bg-red-50',
	},
	{
		tier: 'OTHER',
		label: 'Other / Unique',
		description: 'Unique high-risk industries (e.g., CBD) with volatile rates',
		color: 'border-purple-300 bg-purple-50',
	},
];

type DiscountRateMode = 'same' | 'split';
type AuthFeeMode = 'same' | 'split';

const RiskTierStep: React.FC<RiskTierStepProps> = ({
	configuration,
	onUpdate,
	hideNavigation = false,
}) => {
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [discountRateMode, setDiscountRateMode] =
		useState<DiscountRateMode>('same');
	const [authFeeMode, setAuthFeeMode] = useState<AuthFeeMode>('same');

	const updateTier = (
		tier: RiskTier,
		updates: Partial<RevenueConfigTierInput>,
	) => {
		const newTiers = {
			...configuration.tiers,
			[tier]: {
				...configuration.tiers[tier],
				...updates,
			},
		};
		onUpdate({ tiers: newTiers });

		// Clear error for this field
		const errorKey = `${tier}_${Object.keys(updates)[0]}`;
		if (errors[errorKey]) {
			const newErrors = { ...errors };
			delete newErrors[errorKey];
			setErrors(newErrors);
		}
	};

	const validateField = (
		tier: RiskTier,
		field: string,
		value: number | undefined,
	) => {
		const errorKey = `${tier}_${field}`;

		if (value === undefined || value < 0) {
			setErrors((prev) => ({
				...prev,
				[errorKey]: 'Value must be 0 or greater',
			}));
			return false;
		}

		if (field === 'agent_revenue_share_percent' && value > 100) {
			setErrors((prev) => ({
				...prev,
				[errorKey]: 'Value cannot exceed 100%',
			}));
			return false;
		}

		return true;
	};

	const addMonthlyFee = (tier: RiskTier) => {
		const tierConfig = configuration.tiers[tier];
		const currentFees = tierConfig.monthly_fee_costs || [];
		const newFees = [...currentFees, { label: '', amount: 0 }];
		updateTier(tier, { monthly_fee_costs: newFees });
	};

	const removeMonthlyFee = (tier: RiskTier, index: number) => {
		const tierConfig = configuration.tiers[tier];
		const currentFees = tierConfig.monthly_fee_costs || [];
		const newFees = currentFees.filter((_, i) => i !== index);
		updateTier(tier, { monthly_fee_costs: newFees });
	};

	const updateMonthlyFee = (
		tier: RiskTier,
		index: number,
		field: 'label' | 'amount',
		value: string | number,
	) => {
		const tierConfig = configuration.tiers[tier];
		const currentFees = tierConfig.monthly_fee_costs || [];
		const newFees = [...currentFees];
		newFees[index] = {
			...newFees[index],
			[field]: value,
		};
		updateTier(tier, { monthly_fee_costs: newFees });
	};

	const formatPercent = (value: number) => `${value.toFixed(2)}%`;
	const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

	// Conversion helpers for basis points
	// Database stores values as PERCENTAGE in decimal form (e.g., 0.0125 = 1.25%)
	// But Clay's calculator needs BASIS POINTS in decimal form (e.g., 0.000125 = 1.25 bps)
	//
	// User enters: 1.25 (meaning 1.25 basis points)
	// Stored as: 0.0125 (percentage format for backward compatibility)
	// Calculator converts: 0.0125 / 100 = 0.000125 (bps decimal for calculation)
	//
	// 1 basis point = 0.01% = 0.0001 (decimal multiplier)
	// So 1.25 bps = 0.0125% = 0.000125 (decimal multiplier)
	const bpsToStorageFormat = (bps: number) =>
		parseFloat((bps / 100).toFixed(6)); // 1.25 bps → 0.0125 (stored as %)
	const storageFormatToBps = (stored: number) =>
		parseFloat((stored * 100).toFixed(4)); // 0.0125 → 1.25 bps
	const formatBasisPoints = (bps: number) => `${bps.toFixed(2)} bps`;
	const formatAsPercent = (bps: number) => `${(bps / 100).toFixed(4)}%`; // 1.25 bps → 0.0125%

	return (
		<div className="space-y-8">
			<div className="text-center mb-8">
				<h3 className="text-2xl font-bold text-gray-900 mb-3">
					Schedule A Buy Rates by Risk Tier
				</h3>
				<p className="text-gray-600 text-lg">
					Configure buy rates and revenue share for each merchant risk tier
				</p>
				<div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
					<div className="flex items-start">
						<AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
						<div className="text-sm text-blue-800 text-left">
							<strong>Schedule A:</strong> Your buy rates from the processor.
							These are your costs. The agent's profit = (revenue collected from
							merchant) - (these buy rates).
						</div>
					</div>
				</div>
			</div>

			{/* Mode Selectors */}
			<div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
				<h4 className="font-semibold text-gray-900">Configuration Mode</h4>

				{/* Discount Rate Mode */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Discount Rate Mode
					</label>
					<div className="flex gap-4">
						<button
							type="button"
							onClick={() => setDiscountRateMode('same')}
							className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
								discountRateMode === 'same'
									? 'border-purple-500 bg-purple-50 text-purple-900'
									: 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
							}`}
						>
							Same for All Networks
						</button>
						<button
							type="button"
							onClick={() => setDiscountRateMode('split')}
							className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
								discountRateMode === 'split'
									? 'border-purple-500 bg-purple-50 text-purple-900'
									: 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
							}`}
						>
							Split by Card Type (VMD vs Amex)
						</button>
					</div>
					<p className="mt-1 text-xs text-gray-500">
						{discountRateMode === 'same'
							? 'Use same discount rate for all card networks'
							: 'Amex typically charges 10-15 bps higher than Visa/MC/Discover'}
					</p>
				</div>

				{/* Authorization Fee Mode */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Authorization Fee Mode
					</label>
					<div className="flex gap-4">
						<button
							type="button"
							onClick={() => setAuthFeeMode('same')}
							className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
								authFeeMode === 'same'
									? 'border-purple-500 bg-purple-50 text-purple-900'
									: 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
							}`}
						>
							Same for All Transaction Types
						</button>
						<button
							type="button"
							onClick={() => setAuthFeeMode('split')}
							className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
								authFeeMode === 'split'
									? 'border-purple-500 bg-purple-50 text-purple-900'
									: 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
							}`}
						>
							Split by Transaction Type
						</button>
					</div>
					<p className="mt-1 text-xs text-gray-500">
						{authFeeMode === 'same'
							? 'Use same authorization fee for all transaction types'
							: 'Separate fees for Standard Auth, PIN Debit, and EBT'}
					</p>
				</div>
			</div>

			<div className="space-y-6">
				{RISK_TIERS.map(({ tier, label, description, color }) => {
					const tierConfig = configuration.tiers[tier];
					if (!tierConfig) return null;

					return (
						<div
							key={tier}
							className={`border-2 rounded-lg p-6 ${color} transition-all`}
						>
							<div className="mb-4">
								<h4 className="text-lg font-bold text-gray-900 mb-1">
									{label}
								</h4>
								<p className="text-sm text-gray-600">{description}</p>
							</div>

							<div className="space-y-4">
								{/* Discount Rate Section */}
								{discountRateMode === 'same' ? (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Discount Buy Rate (Basis Points){' '}
											<span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<input
												type="number"
												step="0.001"
												min="0"
												value={storageFormatToBps(
													tierConfig.discount_buy_rate || 0,
												)}
												onChange={(e) => {
													const bpsValue = parseFloat(e.target.value) || 0;
													const storageValue = bpsToStorageFormat(bpsValue);
													updateTier(tier, { discount_buy_rate: storageValue });
													validateField(
														tier,
														'discount_buy_rate',
														storageValue,
													);
												}}
												className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 ${
													errors[`${tier}_discount_buy_rate`]
														? 'border-red-300'
														: 'border-gray-300'
												}`}
												placeholder="e.g., 1.25"
											/>
											<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
												bps
											</div>
										</div>
										{errors[`${tier}_discount_buy_rate`] && (
											<p className="mt-1 text-sm text-red-600">
												{errors[`${tier}_discount_buy_rate`]}
											</p>
										)}
										<p className="mt-1 text-xs text-gray-500">
											Enter basis points. Example: 1.25 bps ={' '}
											{formatAsPercent(1.25)} of volume
										</p>
									</div>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Discount Rate - VMD (Basis Points){' '}
												<span className="text-red-500">*</span>
											</label>
											<div className="relative">
												<input
													type="number"
													step="0.01"
													min="0"
													value={storageFormatToBps(
														tierConfig.discount_buy_rate_vmd ??
															tierConfig.discount_buy_rate ??
															0,
													)}
													onChange={(e) => {
														const bpsValue = parseFloat(e.target.value) || 0;
														updateTier(tier, {
															discount_buy_rate_vmd:
																bpsToStorageFormat(bpsValue),
														});
													}}
													className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
													placeholder="e.g., 1.25"
												/>
												<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
													bps
												</div>
											</div>
											<p className="mt-1 text-xs text-gray-500">
												Visa, Mastercard, Discover
											</p>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Discount Rate - Amex (Basis Points){' '}
												<span className="text-red-500">*</span>
											</label>
											<div className="relative">
												<input
													type="number"
													step="0.01"
													min="0"
													value={storageFormatToBps(
														tierConfig.discount_buy_rate_amex ??
															tierConfig.discount_buy_rate ??
															0,
													)}
													onChange={(e) => {
														const bpsValue = parseFloat(e.target.value) || 0;
														updateTier(tier, {
															discount_buy_rate_amex:
																bpsToStorageFormat(bpsValue),
														});
													}}
													className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
													placeholder="e.g., 1.50"
												/>
												<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
													bps
												</div>
											</div>
											<p className="mt-1 text-xs text-gray-500">
												American Express (typically 10-15 bps higher)
											</p>
										</div>
									</div>
								)}

								{/* Authorization Fee Section */}
								{authFeeMode === 'same' ? (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Authorization Buy Rate{' '}
											<span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<input
												type="number"
												step="0.01"
												min="0"
												value={tierConfig.authorization_buy_rate}
												onChange={(e) => {
													const value = parseFloat(e.target.value);
													updateTier(tier, { authorization_buy_rate: value });
													validateField(tier, 'authorization_buy_rate', value);
												}}
												className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 ${
													errors[`${tier}_authorization_buy_rate`]
														? 'border-red-300'
														: 'border-gray-300'
												}`}
											/>
											<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
												{formatCurrency(tierConfig.authorization_buy_rate || 0)}
											</div>
										</div>
										{errors[`${tier}_authorization_buy_rate`] && (
											<p className="mt-1 text-sm text-red-600">
												{errors[`${tier}_authorization_buy_rate`]}
											</p>
										)}
										<p className="mt-1 text-xs text-gray-500">
											Cost per transaction (e.g., 0.10 = $0.10 per auth)
										</p>
									</div>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Standard Auth <span className="text-red-500">*</span>
											</label>
											<div className="relative">
												<input
													type="number"
													step="0.01"
													min="0"
													value={
														tierConfig.auth_buy_rate_standard ??
														tierConfig.authorization_buy_rate
													}
													onChange={(e) => {
														const value = parseFloat(e.target.value);
														updateTier(tier, { auth_buy_rate_standard: value });
													}}
													className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
												/>
												<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
													{formatCurrency(
														tierConfig.auth_buy_rate_standard ??
															tierConfig.authorization_buy_rate ??
															0,
													)}
												</div>
											</div>
											<p className="mt-1 text-xs text-gray-500">Credit/Debit</p>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												PIN Debit Fee <span className="text-red-500">*</span>
											</label>
											<div className="relative">
												<input
													type="number"
													step="0.01"
													min="0"
													value={
														tierConfig.auth_buy_rate_pin_debit ??
														tierConfig.authorization_buy_rate
													}
													onChange={(e) => {
														const value = parseFloat(e.target.value);
														updateTier(tier, {
															auth_buy_rate_pin_debit: value,
														});
													}}
													className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
												/>
												<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
													{formatCurrency(
														tierConfig.auth_buy_rate_pin_debit ??
															tierConfig.authorization_buy_rate ??
															0,
													)}
												</div>
											</div>
											<p className="mt-1 text-xs text-gray-500">
												PIN transactions
											</p>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												EBT Item Fee <span className="text-red-500">*</span>
											</label>
											<div className="relative">
												<input
													type="number"
													step="0.01"
													min="0"
													value={
														tierConfig.auth_buy_rate_ebt ??
														tierConfig.authorization_buy_rate
													}
													onChange={(e) => {
														const value = parseFloat(e.target.value);
														updateTier(tier, { auth_buy_rate_ebt: value });
													}}
													className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
												/>
												<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
													{formatCurrency(
														tierConfig.auth_buy_rate_ebt ??
															tierConfig.authorization_buy_rate ??
															0,
													)}
												</div>
											</div>
											<p className="mt-1 text-xs text-gray-500">
												EBT/Non-bankcard
											</p>
										</div>
									</div>
								)}

								{/* Batch Fee */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Batch Fee Rate (Optional)
									</label>
									<div className="relative">
										<input
											type="number"
											step="0.01"
											min="0"
											value={tierConfig.batch_fee_rate || 0}
											onChange={(e) => {
												const value = parseFloat(e.target.value) || 0;
												updateTier(tier, { batch_fee_rate: value });
											}}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
										/>
										<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
											{formatCurrency(tierConfig.batch_fee_rate || 0)}
										</div>
									</div>
									<p className="mt-1 text-xs text-gray-500">
										Per-batch cost (calculated as rate × 30 days)
									</p>
								</div>

								{/* Agent Revenue Share */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Agent Revenue Share <span className="text-red-500">*</span>
									</label>
									<div className="relative">
										<input
											type="number"
											step="0.01"
											min="0"
											max="100"
											value={tierConfig.agent_revenue_share_percent}
											onChange={(e) => {
												const value = parseFloat(e.target.value);
												updateTier(tier, {
													agent_revenue_share_percent: value,
												});
												validateField(
													tier,
													'agent_revenue_share_percent',
													value,
												);
											}}
											className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500 ${
												errors[`${tier}_agent_revenue_share_percent`]
													? 'border-red-300'
													: 'border-gray-300'
											}`}
										/>
										<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 text-sm">
											{formatPercent(
												tierConfig.agent_revenue_share_percent || 0,
											)}
										</div>
									</div>
									{errors[`${tier}_agent_revenue_share_percent`] && (
										<p className="mt-1 text-sm text-red-600">
											{errors[`${tier}_agent_revenue_share_percent`]}
										</p>
									)}
									<p className="mt-1 text-xs text-gray-500">
										Agent's percentage of gross margin (0-100%)
									</p>
								</div>

								{/* Dynamic Monthly Fees */}
								<div className="space-y-3">
									<div className="flex items-center justify-between">
										<label className="block text-sm font-medium text-gray-700">
											Monthly Fee Costs (Optional)
										</label>
										<button
											type="button"
											onClick={() => addMonthlyFee(tier)}
											className="flex items-center gap-1 px-3 py-1 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
										>
											<Plus className="w-4 h-4" />
											Add Fee
										</button>
									</div>

									{(tierConfig.monthly_fee_costs || []).map((fee, index) => (
										<div key={index} className="flex gap-2">
											<input
												type="text"
												placeholder="Label (e.g., 'Gateway Fee')"
												value={fee.label}
												onChange={(e) =>
													updateMonthlyFee(tier, index, 'label', e.target.value)
												}
												className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
											/>
											<input
												type="number"
												step="0.01"
												min="0"
												placeholder="Amount"
												value={fee.amount}
												onChange={(e) =>
													updateMonthlyFee(
														tier,
														index,
														'amount',
														parseFloat(e.target.value) || 0,
													)
												}
												className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
											/>
											<button
												type="button"
												onClick={() => removeMonthlyFee(tier, index)}
												className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
											>
												<Trash2 className="w-5 h-5" />
											</button>
										</div>
									))}

									{(tierConfig.monthly_fee_costs || []).length === 0 && (
										<p className="text-sm text-gray-500 italic">
											No monthly fees added. Click "Add Fee" to add one.
										</p>
									)}

									<p className="text-xs text-gray-500">
										Fixed monthly costs from Schedule A (e.g., gateway fees, PCI
										compliance)
									</p>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default RiskTierStep;
